// CC3.MVP W3c · Admin Portal Management panel.
//
// Renders inside Contact Detail drawer. Displays current portal
// account state and exposes admin actions:
//   - Invite / Reissue activation (POST /admin/contacts/:cid/portal/invite)
//   - Copy activation URL (revealed only immediately after invite/reissue)
//   - Disable portal access
//   - Enable / Re-enable portal access
//   - Reset password URL (revealed only immediately after generation)
//
// One-time URL rule: raw activation/reset URLs are held in local
// component state only, cleared on unmount/reload, and never re-fetched
// after the user dismisses them.

import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiError } from "@/lib/errors";
import { Loader2, Copy, Check, Clock, ShieldCheck, ShieldOff, RefreshCcw, KeyRound, Send } from "lucide-react";

const stateLabel = (s) =>
  ({ active: "Active", disabled: "Disabled", pending: "Pending activation" }[s] || (s ? s : "—"));

const stateColor = (s) => {
  if (s === "active") return "bg-touring-lime/20 text-carbon border-touring-lime";
  if (s === "disabled") return "bg-critical-red/10 text-critical-red border-critical-red";
  if (s === "pending") return "bg-amber-hold/15 text-amber-hold border-amber-hold";
  return "bg-chrome-mist text-steel border-coach-silver";
};

function OneTimeUrlBanner({ url, kind, expiresAt, onDismiss }) {
  const [copied, setCopied] = useState(false);
  if (!url) return null;
  // Absolute URL for copy convenience — trims to path if backend already returns path.
  const absolute = url.startsWith("http")
    ? url
    : `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(absolute);
    } catch {
      // Fallback: temporary textarea trick — never fails silently in preview envs.
      const ta = document.createElement("textarea");
      ta.value = absolute;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success(`${kind === "reset" ? "Reset" : "Activation"} URL copied`);
  };
  return (
    <div
      data-testid={`portal-onetime-${kind}`}
      className="border border-touring-lime bg-touring-lime/10 rounded-sm p-3 text-[12.5px]"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-carbon">
            {kind === "reset" ? "Password reset URL" : "Activation URL"} · shown once
          </div>
          <div className="text-[11.5px] text-steel mt-0.5">
            Send this to the contact. The URL is single-use; it will not be shown again after you
            dismiss this panel or reload.
            {expiresAt && (
              <> · Expires {new Date(expiresAt).toLocaleString()}</>
            )}
          </div>
          <div className="mt-2 flex items-stretch gap-2">
            <input
              readOnly
              value={absolute}
              data-testid={`portal-onetime-${kind}-url`}
              className="flex-1 min-w-0 border border-coach-silver rounded-sm px-2 py-1.5 font-mono text-[11.5px] bg-white"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={doCopy}
              data-testid={`portal-onetime-${kind}-copy`}
              className="inline-flex items-center gap-1 bg-coach-blue hover:bg-deep-road-navy text-white text-[12px] px-3 py-1.5 rounded-sm"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
        <button
          onClick={onDismiss}
          data-testid={`portal-onetime-${kind}-dismiss`}
          className="text-steel hover:text-carbon text-[12px] px-2 py-1"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

export function AdminPortalPanel({ contactId, contactEmail }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "invite" | "reissue" | "enable" | "disable" | "reset"
  const [oneTime, setOneTime] = useState(null); // { kind, url, expires_at }
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/contacts/${contactId}/portal`);
      setStatus(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError("Not authenticated.");
      else if (status === 403) setError("You are not authorized to manage portal access.");
      else setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleErr = (e, fallback = "Action failed.") => {
    const s = e?.response?.status;
    const d = e?.response?.data?.detail;
    if (s === 409) {
      return toast.error(
        typeof d === "string" ? d : d?.message || "Conflict — portal already in that state."
      );
    }
    if (s === 422) {
      return toast.error(
        typeof d === "string" ? d : d?.message || "Invalid request (422)."
      );
    }
    if (s === 403) return toast.error("You are not authorized to manage portal access.");
    if (s === 401) return toast.error("Session expired — please sign in again.");
    if (s === 404 && d?.code === "portal_account_not_found") {
      return toast.error("No portal account for this contact.");
    }
    toast.error(apiError(e) || fallback);
  };

  const run = async (kind, req, msg) => {
    setBusy(kind);
    try {
      const r = await req();
      if (msg) toast.success(msg);
      // Reveal the one-time URL if the endpoint returned it.
      if (r?.data?.activation_url) {
        setOneTime({ kind: "activation", url: r.data.activation_url, expires_at: r.data.expires_at });
      } else if (r?.data?.reset_url) {
        setOneTime({ kind: "reset", url: r.data.reset_url, expires_at: r.data.expires_at });
      }
      await load();
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(null);
    }
  };

  const onInvite = () =>
    run("invite", () => api.post(`/admin/contacts/${contactId}/portal/invite`), "Invite issued.");
  const onReissue = () =>
    run("reissue", () => api.post(`/admin/contacts/${contactId}/portal/invite`), "New activation issued.");
  const onDisable = () =>
    run("disable", () => api.post(`/admin/contacts/${contactId}/portal/disable`), "Portal access disabled.");
  const onEnable = () =>
    run("enable", () => api.post(`/admin/contacts/${contactId}/portal/enable`), "Portal access enabled.");
  const onReset = () =>
    run("reset", () => api.post(`/admin/contacts/${contactId}/portal/reset-password`), "Reset URL issued.");

  if (loading) {
    return (
      <div className="py-8 grid place-items-center" data-testid="portal-panel-loading">
        <Loader2 size={18} className="animate-spin text-steel" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="portal-panel-error"
        className="border border-critical-red bg-critical-red/10 rounded-sm p-3 text-[12.5px] text-critical-red"
      >
        {error}
      </div>
    );
  }

  const exists = !!status?.exists;
  const state = status?.activation_state || null;

  return (
    <div className="space-y-3" data-testid="admin-portal-panel">
      <div className="border border-coach-silver rounded-sm p-3 bg-white">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-widest text-steel">
              Portal account
            </div>
            <div className="text-[13.5px] font-medium text-carbon truncate max-w-[260px]">
              {status?.email || contactEmail || "—"}
            </div>
            {status?.last_portal_login_at && (
              <div className="text-[11px] text-steel mt-0.5 flex items-center gap-1">
                <Clock size={11} /> Last login {new Date(status.last_portal_login_at).toLocaleString()}
              </div>
            )}
            {status?.activation_expires_at && state !== "active" && (
              <div className="text-[11px] text-steel mt-0.5">
                Activation expires {new Date(status.activation_expires_at).toLocaleString()}
              </div>
            )}
          </div>
          <span
            data-testid="portal-status-badge"
            className={`text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-sm border ${stateColor(
              exists ? state : "none"
            )}`}
          >
            {exists ? stateLabel(state) : "No portal account"}
          </span>
        </div>
      </div>

      {oneTime && (
        <OneTimeUrlBanner
          url={oneTime.url}
          kind={oneTime.kind}
          expiresAt={oneTime.expires_at}
          onDismiss={() => setOneTime(null)}
        />
      )}

      <div className="flex flex-wrap gap-2">
        {!exists && (
          <button
            onClick={onInvite}
            disabled={busy !== null}
            data-testid="portal-action-invite"
            className="inline-flex items-center gap-1.5 bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] px-3 py-1.5 rounded-sm disabled:opacity-60"
          >
            <Send size={13} /> {busy === "invite" ? "Inviting…" : "Invite to portal"}
          </button>
        )}
        {exists && (
          <>
            <button
              onClick={onReissue}
              disabled={busy !== null}
              data-testid="portal-action-reissue"
              className="inline-flex items-center gap-1.5 border border-coach-silver bg-white hover:bg-road-white text-carbon text-[12.5px] px-3 py-1.5 rounded-sm disabled:opacity-60"
            >
              <RefreshCcw size={13} />
              {busy === "reissue" ? "Reissuing…" : "Reissue activation URL"}
            </button>
            {state === "active" ? (
              <button
                onClick={onDisable}
                disabled={busy !== null}
                data-testid="portal-action-disable"
                className="inline-flex items-center gap-1.5 border border-critical-red bg-white hover:bg-critical-red/5 text-critical-red text-[12.5px] px-3 py-1.5 rounded-sm disabled:opacity-60"
              >
                <ShieldOff size={13} />
                {busy === "disable" ? "Disabling…" : "Disable portal access"}
              </button>
            ) : (
              <button
                onClick={onEnable}
                disabled={busy !== null}
                data-testid="portal-action-enable"
                className="inline-flex items-center gap-1.5 border border-touring-lime bg-touring-lime/10 hover:bg-touring-lime/20 text-carbon text-[12.5px] px-3 py-1.5 rounded-sm disabled:opacity-60"
              >
                <ShieldCheck size={13} />
                {busy === "enable" ? "Enabling…" : "Enable portal access"}
              </button>
            )}
            {state === "active" && (
              <button
                onClick={onReset}
                disabled={busy !== null}
                data-testid="portal-action-reset"
                className="inline-flex items-center gap-1.5 border border-coach-silver bg-white hover:bg-road-white text-carbon text-[12.5px] px-3 py-1.5 rounded-sm disabled:opacity-60"
              >
                <KeyRound size={13} />
                {busy === "reset" ? "Issuing…" : "Send password reset URL"}
              </button>
            )}
          </>
        )}
      </div>

      <div className="text-[11px] text-steel">
        Actions are recorded in the audit log. One-time URLs are shown only immediately after
        generation — dismissing or reloading this panel discards them.
      </div>
    </div>
  );
}

export default AdminPortalPanel;
